// Landing — v2 (BL-018 Product Positioning Realignment, EPIC 0)
//
// Public marketing page. No Layout wrapper (this is the / route in the
// public-routes block in App.js). Everything renders inside this single
// file with design-system tokens only (no hex).
//
// Positioning (authoritative, ADR-025): Rentava is
//   "Opportunity Intelligence for RV and Outdoor Hospitality Investors"
// — NOT an STR / Airbnb / revenue / deal calculator. Public messaging
// describes OUTCOMES only. It never exposes scoring formulas, source
// weighting, confidence models, or proprietary qualification workflows
// (RS-022 disclosure guardrails).
//
// First vertical slice: Visitor → Landing Page → Waitlist.
// The waitlist is intentionally minimal (First Name + Email). It is a
// client-only conversion surface: BL-018 scope excludes backend / Netlify /
// Supabase work, so submission shows the success state and fires the
// waitlist_signup analytics event through the EXISTING PostHog wrapper.
// Durable persistence is a separate, future governance item.
//
// Mobile-first: above the fold carries only headline + subheadline + the two
// primary CTAs (Join Waitlist / See How It Works) + a lightweight trust bar —
// no large graphics, no heavy animation. Below-fold sections use a single,
// light IntersectionObserver fade per Reveal instance.

import React, { useEffect, useRef, useState } from 'react';
import { track } from '../lib/analytics';
import { joinWaitlist } from '../lib/waitlist';
import {
  captureAttribution,
  getAttribution,
  getPlanIntent,
  trackFunnel,
  FUNNEL_EVENTS,
} from '../lib/attribution';

// =========================
// INJECTED STYLES — light scroll-reveal + nav transitions + mobile collapse
// =========================
const STYLES_CSS = `
  .reveal {
    opacity: 0;
    transform: translateY(16px);
    transition:
      opacity 500ms cubic-bezier(0.4, 0, 0.2, 1),
      transform 500ms cubic-bezier(0.4, 0, 0.2, 1);
    will-change: opacity, transform;
  }
  .reveal.is-visible {
    opacity: 1;
    transform: translateY(0);
  }

  .rv-nav {
    transition:
      background-color 200ms cubic-bezier(0.4, 0, 0.2, 1),
      backdrop-filter 200ms cubic-bezier(0.4, 0, 0.2, 1),
      border-color 200ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .rv-lift {
    transition:
      transform 200ms cubic-bezier(0.4, 0, 0.2, 1),
      border-color 200ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .rv-lift:hover {
    transform: translateY(-4px);
    border-color: var(--color-amber);
  }

  /* Honor reduced-motion: disable reveal transforms entirely. */
  @media (prefers-reduced-motion: reduce) {
    .reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
    .rv-lift { transition: none !important; }
  }

  /* Nav anchors collapse on narrow screens so the bar stays usable at 375px —
     the dominant Join Waitlist CTA stays. */
  @media (max-width: 760px) {
    .rv-nav-anchors { display: none !important; }
  }
`;

// =========================
// ICONS — inline outline paths
// =========================
const ICON_PATHS = {
  search:        <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>,
  'check-badge': <path d="M9 12l2 2 4-4M12 3l2.1 1.5 2.6-.3 1 2.4 2.2 1.4-.6 2.5.6 2.5-2.2 1.4-1 2.4-2.6-.3L12 21l-2.1-1.5-2.6.3-1-2.4-2.2-1.4.6-2.5-.6-2.5 2.2-1.4 1-2.4 2.6.3z" />,
  map:           <path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3zM9 3v15M15 6v15" />,
  building:      <path d="M3 21h18M5 21V5l7-2v18M19 21V9l-7-4M9 8h0M9 12h0M9 16h0" />,
  target:        <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>,
  layers:        <path d="M12 3 3 8l9 5 9-5-9-5z M3 13l9 5 9-5 M3 18l9 5 9-5" />,
  compass:       <><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" /></>,
  'arrow-right': <path d="M5 12h14m-5-5l5 5-5 5" />,
  check:         <path d="M5 12l4.5 4.5L19 7" />,
};
function Icon({ name, size = 24, color = 'currentColor', strokeWidth = 1.75, style }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      aria-hidden="true"
      style={{ flexShrink: 0, ...style }}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

// =========================
// REVEAL — light IntersectionObserver fade-in wrapper (below-fold only)
// =========================
function Reveal({ children, delay = 0, as: As = 'div', style, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <As
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </As>
  );
}

// =========================
// WORDMARK — "Renta" white + "va" amber
// =========================
function Wordmark({ size = 'var(--text-xl)' }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 'var(--weight-black)',
        fontSize: size,
        letterSpacing: 'var(--tracking-wide)',
        textTransform: 'uppercase',
        color: 'var(--color-white)',
        lineHeight: 1,
      }}
    >
      Renta<span style={{ color: 'var(--color-amber)' }}>va</span>
    </span>
  );
}

// =========================
// SECTION HEADER — eyebrow + headline + optional body
// =========================
function SectionHeader({ eyebrow, headline, body, maxWidth = 760 }) {
  return (
    <div
      style={{
        textAlign: 'center',
        maxWidth,
        margin: '0 auto var(--space-10)',
      }}
    >
      {eyebrow && (
        <Reveal>
          <div
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--weight-bold)',
              letterSpacing: 'var(--tracking-label)',
              textTransform: 'uppercase',
              color: 'var(--color-amber)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {eyebrow}
          </div>
        </Reveal>
      )}
      <Reveal delay={60}>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.75rem, 1rem + 3vw, 2.75rem)',
            fontWeight: 'var(--weight-black)',
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--color-white)',
            margin: 0,
            marginBottom: body ? 'var(--space-4)' : 0,
            lineHeight: 'var(--leading-tight)',
          }}
        >
          {headline}
        </h2>
      </Reveal>
      {body && (
        <Reveal delay={120}>
          <p
            style={{
              fontSize: 'var(--text-lg)',
              color: 'var(--color-steel-lt)',
              lineHeight: 'var(--leading-normal)',
              margin: 0,
            }}
          >
            {body}
          </p>
        </Reveal>
      )}
    </div>
  );
}

// =========================
// SHARED CTA HELPERS — analytics-wired links
// =========================
// "Join Waitlist" CTA: anchor to #waitlist + fire landing_waitlist_click.
function WaitlistLink({ children = 'Join Waitlist', className = 'btn btn-primary', style, location }) {
  return (
    <a
      href="#waitlist"
      className={className}
      style={style}
      onClick={() => track('landing_waitlist_click', { location })}
    >
      {children}
    </a>
  );
}

// =========================
// NAVBAR — fixed, 64px, blur on scroll. Sole conversion CTA is Join Waitlist.
// =========================
function NavBar({ scrolled }) {
  const anchorStyle = {
    color: 'var(--color-white-dim)',
    textDecoration: 'none',
    fontSize: 'var(--text-base)',
    fontWeight: 'var(--weight-medium)',
    transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  };
  return (
    <nav
      className="rv-nav"
      aria-label="Primary"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 64,
        zIndex: 100,
        background: scrolled
          ? 'color-mix(in srgb, var(--color-navy) 88%, transparent)'
          : 'var(--color-navy)',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled
          ? '1px solid var(--color-navy-border)'
          : '1px solid transparent',
        display: 'flex',
        alignItems: 'center',
        padding: '0 var(--space-6)',
      }}
    >
      <a href="#top" style={{ textDecoration: 'none' }} aria-label="Rentava home">
        <Wordmark />
      </a>

      <div
        className="rv-nav-anchors"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-6)',
          marginLeft: 'var(--space-10)',
        }}
      >
        <a href="#how-it-works" style={anchorStyle}>How it works</a>
        <a href="#who-its-for" style={anchorStyle}>Who it's for</a>
      </div>

      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
        }}
      >
        <WaitlistLink className="btn btn-primary btn-sm" location="nav" />
      </div>
    </nav>
  );
}

// =========================
// SECTION 1 — HERO (mobile-first, no above-fold graphic)
// =========================
function Hero() {
  return (
    <section
      id="top"
      style={{
        padding: 'calc(64px + var(--space-12)) var(--space-6) var(--space-12)',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--weight-bold)',
            letterSpacing: 'var(--tracking-label)',
            textTransform: 'uppercase',
            color: 'var(--color-amber)',
            marginBottom: 'var(--space-4)',
          }}
        >
          Opportunity Intelligence
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2.25rem, 1.4rem + 4vw, 4rem)',
            fontWeight: 'var(--weight-black)',
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
            color: 'var(--color-white)',
            margin: 0,
            marginBottom: 'var(--space-5)',
          }}
        >
          Find RV &amp; Outdoor Hospitality Opportunities{' '}
          <span style={{ color: 'var(--color-amber)' }}>Before Everyone Else.</span>
        </h1>

        <p
          style={{
            fontSize: 'var(--text-lg)',
            color: 'var(--color-steel-lt)',
            lineHeight: 'var(--leading-normal)',
            maxWidth: 720,
            margin: '0 auto var(--space-8)',
          }}
        >
          Rentava helps investors identify, qualify, and evaluate RV parks,
          campgrounds, RV lots, and outdoor hospitality opportunities using
          market intelligence, property intelligence, and opportunity scoring.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-8)',
          }}
        >
          <WaitlistLink className="btn btn-primary btn-lg" location="hero" />
          <a href="#how-it-works" className="btn btn-secondary btn-lg">
            See How It Works
          </a>
        </div>

        {/* Trust bar — lightweight text chips, no graphics. */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {['RV Parks', 'Campgrounds', 'RV Resorts', 'RV Lots', 'Outdoor Hospitality'].map((seg) => (
            <span
              key={seg}
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-white-dim)',
                background: 'var(--color-navy-mid)',
                border: '1px solid var(--color-navy-border)',
                borderRadius: 'var(--radius-full)',
                padding: 'var(--space-1) var(--space-4)',
              }}
            >
              {seg}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// =========================
// SECTION 2 — PROBLEM
// =========================
function ProblemSection() {
  const pains = [
    {
      title: 'Too many properties',
      body: 'Endless listings and parcels, with no fast way to separate the few worth your time from the noise.',
    },
    {
      title: 'Too much research',
      body: 'Hours of manual digging across scattered sources before you can even decide whether to look closer.',
    },
    {
      title: 'Too many bad opportunities',
      body: 'Deals that look promising on the surface but fall apart once the real constraints come to light.',
    },
  ];
  return (
    <section
      id="problem"
      style={{
        background: 'var(--color-navy-lt)',
        borderTop: '1px solid var(--color-navy-border)',
        borderBottom: '1px solid var(--color-navy-border)',
        padding: 'var(--space-16) var(--space-6)',
      }}
    >
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <SectionHeader
          eyebrow="The problem"
          headline={
            <>
              Investors Don&apos;t Need More Data.
              <br />
              They Need Better Decisions.
            </>
          }
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 'var(--space-5)',
          }}
        >
          {pains.map((p, i) => (
            <Reveal key={p.title} delay={i * 100}>
              <div className="card rv-lift" style={{ height: '100%' }}>
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-xl)',
                    fontWeight: 'var(--weight-bold)',
                    color: 'var(--color-white)',
                    marginBottom: 'var(--space-3)',
                    lineHeight: 'var(--leading-tight)',
                  }}
                >
                  {p.title}
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-white-dim)',
                    lineHeight: 'var(--leading-normal)',
                    margin: 0,
                  }}
                >
                  {p.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// =========================
// SECTION 3 — OPPORTUNITY FRAMEWORK (outcomes only; no implementation detail)
// =========================
function OpportunityFramework() {
  const steps = [
    { icon: 'search',      label: 'Discovery',             outcome: 'Surface opportunities — including off-market ones — across RV and outdoor hospitality.' },
    { icon: 'check-badge', label: 'Qualification',         outcome: 'Filter out opportunities that don’t fit before you invest time in them.' },
    { icon: 'map',         label: 'Market Intelligence',   outcome: 'Understand whether the surrounding market truly supports the opportunity.' },
    { icon: 'building',    label: 'Property Intelligence',  outcome: 'See what matters about the property itself, in one place.' },
    { icon: 'target',      label: 'Opportunity Score',     outcome: 'Get a clear, comparable read on how strong each opportunity is.' },
  ];
  return (
    <section id="how-it-works" style={{ padding: 'var(--space-16) var(--space-6)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionHeader
          eyebrow="How it works"
          headline="A clear path from opportunity to decision."
          body="Rentava turns scattered research into one guided flow — so you spend your time on opportunities worth pursuing."
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 'var(--space-4)',
            alignItems: 'stretch',
          }}
        >
          {steps.map((s, i) => (
            <Reveal key={s.label} delay={i * 90} style={{ display: 'flex' }}>
              <div className="card rv-lift" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    marginBottom: 'var(--space-3)',
                  }}
                >
                  <Icon name={s.icon} size={22} color="var(--color-amber)" />
                  <span
                    style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--weight-bold)',
                      color: 'var(--color-muted)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--weight-bold)',
                    color: 'var(--color-white)',
                    lineHeight: 'var(--leading-tight)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  {s.label}
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-white-dim)',
                    lineHeight: 'var(--leading-normal)',
                    margin: 0,
                  }}
                >
                  {s.outcome}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// =========================
// SECTION 4 — WHO IT'S FOR
// =========================
function WhoItsFor() {
  const cards = [
    { icon: 'map',      title: 'RV Park Investors',             body: 'Find and qualify RV parks and resorts worth pursuing — faster.' },
    { icon: 'target',   title: 'RV Lot Investors',              body: 'Evaluate individual lots and pads with clarity, even off-market.' },
    { icon: 'compass',  title: 'Outdoor Hospitality Operators',  body: 'Spot opportunities to expand or reposition outdoor hospitality assets.' },
    { icon: 'building', title: 'Developers',                    body: 'Identify and validate development opportunities before committing.' },
  ];
  return (
    <section
      id="who-its-for"
      style={{
        background: 'var(--color-navy-lt)',
        borderTop: '1px solid var(--color-navy-border)',
        borderBottom: '1px solid var(--color-navy-border)',
        padding: 'var(--space-16) var(--space-6)',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionHeader eyebrow="Who it's for" headline="Built for serious opportunity hunters." />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 'var(--space-5)',
          }}
        >
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={(i % 4) * 90}>
              <div className="card rv-lift" style={{ height: '100%' }}>
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <Icon name={c.icon} size={28} color="var(--color-amber)" />
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--weight-bold)',
                    color: 'var(--color-white)',
                    lineHeight: 'var(--leading-tight)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  {c.title}
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-white-dim)',
                    lineHeight: 'var(--leading-normal)',
                    margin: 0,
                  }}
                >
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// =========================
// SECTION 5 — HOW RENTAVA HELPS
// =========================
function HowRentavaHelps() {
  const cards = [
    { icon: 'search',  title: 'Find Opportunities',      body: 'Stop scrolling endless listings. Rentava brings the opportunities that fit to the surface.' },
    { icon: 'compass', title: 'Evaluate Opportunities',  body: 'Replace hours of scattered research with one clear, outcome-focused view of each opportunity.' },
    { icon: 'target',  title: 'Prioritize Opportunities', body: 'Know which opportunities deserve your time first — and which to walk away from.' },
  ];
  return (
    <section id="helps" style={{ padding: 'var(--space-16) var(--space-6)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionHeader
          eyebrow="How Rentava helps"
          headline="Find. Evaluate. Prioritize."
          body="Proprietary opportunity intelligence and multi-source market intelligence, in one investor-focused workflow."
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 'var(--space-5)',
          }}
        >
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 100}>
              <div className="card rv-lift" style={{ height: '100%' }}>
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <Icon name={c.icon} size={28} color="var(--color-amber)" />
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-xl)',
                    fontWeight: 'var(--weight-bold)',
                    color: 'var(--color-white)',
                    lineHeight: 'var(--leading-tight)',
                    marginBottom: 'var(--space-3)',
                  }}
                >
                  {c.title}
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-white-dim)',
                    lineHeight: 'var(--leading-normal)',
                    margin: 0,
                  }}
                >
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// Optional investor-segment options for lightweight audience qualification.
// Values are the whitelist persisted by netlify/functions/waitlist-signup.js;
// labels mirror the landing-page "Who It's For" audiences.
const SEGMENT_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'rv_park_investor', label: 'RV Park Investor' },
  { value: 'rv_lot_investor', label: 'RV Lot Investor' },
  { value: 'outdoor_hospitality_operator', label: 'Outdoor Hospitality Operator' },
  { value: 'developer', label: 'Developer' },
  { value: 'other', label: 'Other' },
];

// =========================
// SECTION 6 — WAITLIST (dedicated conversion section)
// =========================
// BL-018b — the waitlist is now a durable, owned audience: on valid submit we
// optimistically show the approved success state, fire waitlist_signup through
// the PostHog wrapper, AND persist the signup via lib/waitlist (service-role
// Netlify function → waitlist_signups). Persistence is fire-and-forget so a
// network failure never breaks the visitor's "you're on the list" experience
// (capture is prioritized over latency/abandonment — ADR-028 / RS-025).
//
// Fields by priority (PO guidance): Email > First name > optional Segment.
// A required consent checkbox makes durable PII capture affirmative; the
// reviewed consent/Privacy-Policy language is the gated go-live dependency.
export function WaitlistForm() {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [segment, setSegment] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // BL-038 Story 38.1 — first-touch source attribution (utm_*). Captured once
  // when the waitlist surface mounts so a direct landing with utm params is
  // still attributed even if the visitor never visits /pricing.
  useEffect(() => {
    captureAttribution();
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    // Email + first name + affirmative consent are required; segment is optional.
    if (!firstName.trim() || !email.trim() || !consent) return;

    // Optimistic: show success + fire analytics synchronously so capture adds
    // no latency. Persistence is fire-and-forget and never throws (lib/waitlist
    // returns { data, error }); a failure is swallowed so the promise to the
    // visitor is always honored.
    setSubmitted(true);
    track('waitlist_signup', { source: 'landing_v2', segment: segment || null });
    // BL-038 Story 38.1/38.3 — carry the funnel's demand + source signals into
    // the durable audience: the plan the visitor chose on /pricing (plan intent)
    // and the first-touch utm attribution. Both are optional/advisory and never
    // block the optimistic "you're on the list" experience.
    const planInterest = getPlanIntent();
    const attribution = getAttribution();
    trackFunnel(FUNNEL_EVENTS.WAITLIST_SUBMIT, {
      tier: planInterest || null,
      segment: segment || null,
    });
    joinWaitlist({
      email: email.trim(),
      firstName: firstName.trim(),
      segment: segment || null,
      source: 'landing_v2',
      planInterest,
      attribution,
    }).catch(() => {});
  }

  if (submitted) {
    return (
      <div
        className="card-accent"
        role="status"
        style={{ textAlign: 'center', maxWidth: 520, margin: '0 auto' }}
      >
        <div style={{ marginBottom: 'var(--space-3)', display: 'flex', justifyContent: 'center' }}>
          <Icon name="check" size={36} color="var(--color-amber)" strokeWidth={2.5} />
        </div>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--weight-black)',
            color: 'var(--color-white)',
            marginBottom: 'var(--space-2)',
          }}
        >
          You&apos;re on the list.
        </div>
        <p
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-white-dim)',
            lineHeight: 'var(--leading-normal)',
            margin: 0,
          }}
        >
          We&apos;ll notify you when new investor opportunities become available.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ maxWidth: 520, margin: '0 auto', textAlign: 'left' }}
    >
      <div className="input-group">
        <label className="input-label" htmlFor="wl-first-name">First name</label>
        <input
          id="wl-first-name"
          className="input"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          autoComplete="given-name"
          required
        />
      </div>
      <div className="input-group" style={{ marginTop: 'var(--space-3)' }}>
        <label className="input-label" htmlFor="wl-email">Email</label>
        <input
          id="wl-email"
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
      </div>
      <div className="input-group" style={{ marginTop: 'var(--space-3)' }}>
        <label className="input-label" htmlFor="wl-segment">
          Which best describes you? (optional)
        </label>
        <select
          id="wl-segment"
          className="input"
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
        >
          {SEGMENT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <label
        htmlFor="wl-consent"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-4)',
          fontSize: 'var(--text-sm)',
          color: 'var(--color-white-dim)',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        <input
          id="wl-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          I agree to be contacted about Rentava and accept the{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-amber)', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>
      <button
        type="submit"
        className="btn btn-primary btn-lg"
        style={{ width: '100%', marginTop: 'var(--space-5)' }}
      >
        Join the Waitlist
      </button>
    </form>
  );
}

function WaitlistSection() {
  return (
    <section
      id="waitlist"
      style={{
        background: 'var(--color-navy-lt)',
        borderTop: '1px solid var(--color-navy-border)',
        borderBottom: '1px solid var(--color-navy-border)',
        padding: 'var(--space-16) var(--space-6)',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <SectionHeader
          eyebrow="Get early access"
          headline="Join the Waitlist"
          body="Be first to find RV and outdoor hospitality opportunities the moment Rentava opens. No credit card required."
        />
        <Reveal>
          <WaitlistForm />
        </Reveal>
      </div>
    </section>
  );
}

// =========================
// SECTION 7 — FAQ
// =========================
function FAQSection() {
  const faqs = [
    {
      q: 'What is Rentava?',
      a: 'Rentava is opportunity intelligence for RV and outdoor hospitality investors — it helps you identify, qualify, and evaluate opportunities, not just crunch numbers on a property you already found.',
    },
    {
      q: 'What kinds of opportunities does Rentava cover?',
      a: 'RV parks, campgrounds, RV resorts, RV lots, and outdoor hospitality assets across the United States.',
    },
    {
      q: 'How is this different from a property analysis tool?',
      a: 'Most tools start after you’ve already found a property and only run the math. Rentava helps you discover, qualify, and prioritize opportunities first — so you spend your time on the ones worth pursuing.',
    },
    {
      q: 'Is Rentava available now?',
      a: 'We’re opening access in stages. Join the waitlist and we’ll notify you when new investor opportunities become available.',
    },
    {
      q: 'Do I need a credit card to join the waitlist?',
      a: 'No. Joining the waitlist takes a first name and an email — nothing else.',
    },
  ];
  return (
    <section id="faq" style={{ padding: 'var(--space-16) var(--space-6)' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <SectionHeader eyebrow="Questions" headline="Frequently Asked Questions" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {faqs.map((f, i) => (
            <Reveal key={f.q} delay={i * 70}>
              <div className="card">
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--weight-bold)',
                    color: 'var(--color-white)',
                    marginBottom: 'var(--space-2)',
                    lineHeight: 'var(--leading-tight)',
                  }}
                >
                  {f.q}
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-white-dim)',
                    lineHeight: 'var(--leading-normal)',
                    margin: 0,
                  }}
                >
                  {f.a}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// =========================
// SECTION 8 — FOOTER CTA (Join Waitlist — sole conversion CTA)
// =========================
function FooterCTA() {
  return (
    <section style={{ padding: 'var(--space-16) var(--space-6)', textAlign: 'center' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Reveal>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1.875rem, 1rem + 3vw, 3rem)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--color-white)',
              lineHeight: 'var(--leading-tight)',
              margin: 0,
              marginBottom: 'var(--space-4)',
            }}
          >
            The best opportunities go fast.
          </h2>
        </Reveal>
        <Reveal delay={100}>
          <p
            style={{
              fontSize: 'var(--text-lg)',
              color: 'var(--color-steel-lt)',
              lineHeight: 'var(--leading-normal)',
              margin: '0 auto var(--space-6)',
              maxWidth: 600,
            }}
          >
            Join the waitlist and be first to find RV and outdoor hospitality
            opportunities before everyone else.
          </p>
        </Reveal>
        <Reveal delay={180}>
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <WaitlistLink className="btn btn-primary btn-lg" location="footer_cta" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// =========================
// DISCLAIMER BANNER — teal-tinted strip above footer
// =========================
function DisclaimerBanner() {
  return (
    <div
      style={{
        background: 'var(--color-teal-ghost)',
        borderTop: '1px solid var(--color-teal)',
        borderBottom: '1px solid var(--color-teal)',
        padding: 'var(--space-3) var(--space-6)',
      }}
    >
      <p
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-muted)',
          lineHeight: 'var(--leading-normal)',
          textAlign: 'center',
        }}
      >
        For informational purposes only. Not financial, investment, or legal
        advice. Always consult a licensed professional before making investment
        decisions.
      </p>
    </div>
  );
}

// =========================
// FOOTER
// =========================
function Footer() {
  const linkStyle = {
    color: 'var(--color-steel)',
    textDecoration: 'none',
    fontSize: 'var(--text-sm)',
    transition: 'color 200ms cubic-bezier(0.4,0,0.2,1)',
  };
  return (
    <footer
      style={{
        background: 'var(--color-navy)',
        borderTop: '1px solid var(--color-navy-border)',
        padding: 'var(--space-8) var(--space-6)',
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-5)',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Wordmark />
        <nav aria-label="Footer" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-5)' }}>
          <a href="/privacy" style={linkStyle}>Privacy Policy</a>
          <a href="/terms" style={linkStyle}>Terms of Service</a>
          <a href="mailto:support@rentava.io" style={linkStyle}>Support</a>
        </nav>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-muted)' }}>
          © 2026 Rentava LLC · rentava.io
        </div>
      </div>
    </footer>
  );
}

// =========================
// LANDING — composition + scroll-depth analytics
// =========================
export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const depthFired = useRef({ 50: false, 90: false });

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);

      // Scroll-depth analytics — fire each threshold once.
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable > 0) {
        const pct = (window.scrollY / scrollable) * 100;
        if (pct >= 50 && !depthFired.current[50]) {
          depthFired.current[50] = true;
          track('landing_50_percent', {});
        }
        if (pct >= 90 && !depthFired.current[90]) {
          depthFired.current[90] = true;
          track('landing_90_percent', {});
        }
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      style={{
        background: 'var(--color-navy)',
        minHeight: '100vh',
        color: 'var(--color-white)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <style>{STYLES_CSS}</style>

      <NavBar scrolled={scrolled} />
      <Hero />
      <ProblemSection />
      <OpportunityFramework />
      <WhoItsFor />
      <HowRentavaHelps />
      <WaitlistSection />
      <FAQSection />
      <FooterCTA />
      <DisclaimerBanner />
      <Footer />
    </div>
  );
}
